<template>
	<v-card
		variant="outlined"
		class="resource-card-shell"
		data-test="add-repository-card"
	>
		<template v-if="repoData">
			<div class="resource-card">
				<div class="resource-card__title-name sgnl-flex sgnl-align-center">
					<GitRepo24 class="sgnl-flex-shrink-0 sgnl-icon-color-secondary" />
					<a
						class="repo-title sgnl-paragraph-m-medium"
						:href="repoData.html_url"
						target="_blank"
					>
						{{ repoData.full_name }}
					</a>
				</div>
				<div class="resource-card__description">
					<div class="repo-description sgnl-paragraph-s-regular">
						{{ repoData.description }}
					</div>
				</div>
				<div class="resource-card__footer">
					<span class="info-badges sgnl-inline-flex sgnl-align-center sgnl-flex-wrap">
						<span class="repo-badge">
							<Locked16 v-if="repoData.visibility === 'private'" />
							<Earth16 v-else />
							{{ capitalize(repoData.visibility) }}
						</span>
						<span
							v-if="repoData.license"
							class="repo-badge"
						>
							<Scales16 />
							{{ repoData.license.name }}
						</span>
						<span class="repo-badge">
							<Calendar16 />
							{{ formatDate(repoData.updated_at) }}
						</span>
						<span
							v-if="repoData.forks_count"
							class="repo-badge"
						>
							<Fork16 />
							{{ repoData.forks_count }}
						</span>
					</span>
					<div class="sgnl-flex sgnl-align-center sgnl-gap-8">
						<LimitableActionButton
							v-if="!alreadyConnected()"
							variant="flat"
							color="primary"
							tooltipText="Connect to Repository"
							data-test="connect-repo-button"
							:limits-to-check="[UserLimitsE.RepositoryCount]"
							@valid-click="requestRepoConnect"
						>
							<template #btnContent>
								<BookmarkAdd16 />
								<span>Connect</span>
							</template>
						</LimitableActionButton>
						<v-spacer />
						<v-btn
							v-if="alreadyConnected()"
							variant="outlined"
							color="error"
							:loading="repoDisconnectStatus.loading"
							@click="disconnectRepository"
							data-test="disconnect-repo-button"
						>
							<TrashCan16 />
							<v-tooltip activator="parent">Disconnect</v-tooltip>
						</v-btn>
					</div>
				</div>
			</div>
		</template>
		<template v-else>
			<div class="resource-card">
				<div class="resource-card__title-name sgnl-flex sgnl-align-center">
					<SkeletonBlock
						width="var(--sgnl-icon-24)"
						height="var(--sgnl-icon-24)"
					/>
					<SkeletonBlock
						width="60%"
						class="sgnl-paragraph-m-medium"
					/>
				</div>
				<div class="resource-card__description">
					<SkeletonBlock
						width="90%"
						class="sgnl-paragraph-s-regular"
					/>
				</div>
				<div class="resource-card__footer">
					<SkeletonBlock
						width="332px"
						height="var(--sgnl-unit-24)"
					/>
					<div class="sgnl-flex sgnl-align-center sgnl-gap-8">
						<SkeletonBlock
							width="120px"
							btn
						/>
					</div>
				</div>
			</div>
		</template>
	</v-card>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";

// Libraries
import moment from "moment";
import axios from "axios";

// Utilities
import { monitoringCaptureError } from "@/plugins/monitoring";

// Stores
import { useRepositoriesStore } from "@/stores/repositories";
import { useUserStore } from "@/stores/user";

// Types
import type { Endpoints } from "@octokit/types";
import { RemoteAccessState, RepoConnectionConfig } from "@/types/general";
import { UserLimitsE } from "@/js/tierconfig";
import { capitalize } from "@/js/utilities";

// Components
import LimitableActionButton from "@/components/Common/LimitableActionButton.vue";
import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";
import TrashCan16 from "@carbon/icons-vue/es/trash-can/16";
import Scales16 from "@carbon/icons-vue/es/scales/16";
import Calendar16 from "@carbon/icons-vue/es/calendar/16";
import Fork16 from "@carbon/icons-vue/es/fork/16";
import BookmarkAdd16 from "@carbon/icons-vue/es/bookmark--add/16";
import GitRepo24 from "@carbon/icons-vue/es/git--repo/24";
import Earth16 from "@carbon/icons-vue/es/earth/16";
import Locked16 from "@carbon/icons-vue/es/locked/16";
import { signaloidApiClient } from "@/js/signaloidClient";

type ComponentData = {
	repoData: undefined | Endpoints["GET /user/repos"]["response"]["data"][0];
	connectedRepositoryID: undefined | string;
	repoDisconnectStatus: RemoteAccessState;
};

export default defineComponent({
	name: "AddRepositoryCard",
	components: {
		LimitableActionButton,
		SkeletonBlock,
		TrashCan16,
		Scales16,
		Calendar16,
		Fork16,
		BookmarkAdd16,
		GitRepo24,
		Earth16,
		Locked16,
	},
	props: {
		initialRepoData: { type: Object as PropType<undefined | Endpoints["GET /user/repos"]["response"]["data"][0]> },
		repoFullName: { type: String },
	},
	emits: {
		"repo-connection-request": (_data: RepoConnectionConfig) => true,
		"repository-disconnected": (_data: { repositoryID: string }) => true,
	},
	setup() {
		const repositoriesStore = useRepositoriesStore();
		const userStore = useUserStore();
		return { repositoriesStore, userStore, UserLimitsE };
	},
	data: (): ComponentData => ({
		repoData: undefined,
		connectedRepositoryID: undefined,
		repoDisconnectStatus: { loading: false, error: false, message: "" },
	}),
	methods: {
		capitalize,
		formatDate(dateString) {
			return moment(dateString).format("DD MMM YYYY");
		},
		getRepoData(repoFullName) {
			return signaloidApiClient
				.get(`/proxy/github/repos/${repoFullName}`)
				.then((response) => {
					this.repoData = response.data;
				})
				.catch((error) => console.log(error));
		},
		requestRepoConnect() {
			if (this.repoData?.html_url) {
				this.$emit("repo-connection-request", { remoteURL: new URL(this.repoData.html_url) });
			}
		},
		async disconnectRepository() {
			if (this.alreadyConnected() && this.connectedRepositoryID) {
				this.repoDisconnectStatus.loading = true;
				try {
					await this.repositoriesStore.disconnectFromRepository(this.connectedRepositoryID);
					setTimeout(async () => {
						this.repoDisconnectStatus.loading = false;
					}, 1000);
				} catch (error) {
					monitoringCaptureError(error, "Disconnect repository");

					if (axios.isAxiosError(error)) {
						this.repoDisconnectStatus.error = true;
						this.repoDisconnectStatus.message =
							"An error occurred while disconnecting from the repository.";
					}
					this.repoDisconnectStatus.loading = false;
				}
				this.$emit("repository-disconnected", { repositoryID: this.connectedRepositoryID });
			} else {
				console.log("not connected");
			}
		},
		alreadyConnected() {
			let val: any = undefined;
			if (this.repositoriesStore.connectedRepositories) {
				val = this.repositoriesStore.connectedRepositories.find((currentValue) => {
					return currentValue.RemoteURL == this.repoData?.html_url;
				});
			}
			if (val) {
				this.connectedRepositoryID = val.RepositoryID;
				return true;
			} else {
				return false;
			}
		},
	},
	created() {
		this.userStore.getCurrentUserTierDetails();
	},
	mounted() {
		// Seed from props in mounted() rather than data() — props aren't bound during data().
		this.repoData = this.initialRepoData;

		if (!this.repoData && this.repoFullName) {
			this.getRepoData(this.repoFullName);
		}
	},
});
</script>

<style lang="scss" scoped>
@media (max-width: 599px) {
	.resource-card__footer > .sgnl-flex {
		flex-wrap: wrap;
	}

	.resource-card__footer > .sgnl-flex > .v-spacer {
		display: none;
	}

	.resource-card__footer > .sgnl-flex > span {
		display: block;
		width: 100%;
	}

	.resource-card__footer :deep(.v-btn--variant-outlined) {
		width: auto;
		flex: 1;
	}
}
</style>
